<template>
  <div class="w-full px-8 py-6 rounded-md bg-surface ">
    <div class="flex flex-row items-center">
      <AM3Icon
        v-if="icon"
        size="sm"
        class="mr-2"
        type="logo"
      />
      <h3
        class="text-sm font-medium text-primary"
        :data-test-id="generateDataTestId(heading, 'heading')"
      >
        {{ heading }}
      </h3>
      <AM3Icon
        v-if="tooltip && tooltip.length > 0"
        v-tooltip="tooltip"
        size="sm"
        type="information-circle"
        class="ml-2 cursor-pointer"
      />
    </div>

    <div class="mt-4 text-left">
      <span
        :data-test-id="generateDataTestId(heading, 'value')"
        :class=" gradient ? 'from-[#B758F6] text-transparent  bg-gradient-to-r  bg-clip-text via-[#B173E9] via-[#B758F6] to-[#DE5BEC]' : 'text-primary' "
        class="text-4xl font-bold "
      >
        {{ animate ? formatValue(animatedValue) : formatValue(value) }}{{ isNonNumericSource ? '' : suffix }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'

import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'

import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'

const props = defineProps({
  icon: {
    type: String,
    default: null,
  },
  heading: {
    type: String,
    required: true,
  },
  tooltip: {
    type: String,
    default: '',
  },
  value: {
    type: [Number, String],
    required: true,
  },
  startValue: {
    type: [Number, String],
    default: 0,
  },
  animate: {
    type: Boolean,
    default: false,
  },
  duration: {
    type: Number,
    default: 1000,
  },
  gradient: {
    type: Boolean,
    default: false,
  },
  suffix: {
    type: String,
    default: null,
    required: false,
  },
  // Rendered verbatim before the number. Passing the symbol separately rather
  // than interpolated into `value` is deliberate: the previous shape had the
  // caller build `${symbol} ${amount}`, which this component then stripped
  // back out to animate and re-derived to render. That round-trip is what
  // produced both the '£ 0' and the 'N/A0' bugs.
  symbol: {
    type: String,
    default: '',
  },
})

const animatedValue = ref(0)

// Convert string to number. Values still arrive pre-formatted with thousands
// separators (e.g. '2,000.00'), so those are stripped; the symbol no longer
// needs stripping because it is a prop rather than part of the value.
const parseValue = (val: string | number): number => {
  if (typeof val === 'number') return val

  return Number(String(val).replace(/[^0-9.-]/g, '')) || 0
}

const targetValue = computed(() => parseValue(props.value))
const startingValue = computed(() => parseValue(props.startValue))

const animateToValue = () => {
  if (!props.animate) return

  const duration = props.duration
  const start = startingValue.value
  const end = targetValue.value
  const startTime = performance.now()

  // Initialize animated value to the start value
  animatedValue.value = start

  const updateValue = (currentTime: number) => {
    const elapsedTime = currentTime - startTime
    const progress = Math.min(elapsedTime / duration, 1)
    const easedProgress = easeOutQuad(progress)

    // Intermediate frames stay whole for a clean count-up, but the final
    // frame lands on the exact target so cents are not truncated.
    animatedValue.value = progress >= 1
      ? end
      : Math.floor(start + (end - start) * easedProgress)

    if (progress < 1) {
      requestAnimationFrame(updateValue)
    }
  }

  requestAnimationFrame(updateValue)
}

// Easing function for smoother animation
const easeOutQuad = (t: number): number => {
  return t * (2 - t)
}

// True when the source value carries no number at all — 'N/A', '-' and so on.
// Number('') is 0 rather than NaN, so an empty strip has to be caught
// explicitly. The suffix is suppressed for these too, or a rate with no data
// renders as '-%'.
const isNonNumericSource = computed(() => {
  if (typeof props.value !== 'string') return false
  const digits = props.value.replace(/[^0-9.-]/g, '')
  return digits === '' || isNaN(Number(digits))
})

// Format value with currency and commas
const formatValue = (val: number | string): string => {
  // Whether this is numeric at all is a property of the SOURCE value, not of
  // `val` — on the animated branch `val` is always a number, so deciding from
  // it let 'N/A' render as 'N/A0' and '-' as '0'.
  if (isNonNumericSource.value) return props.value as string

  const numValue = Number(String(val).replace(/[^0-9.-]/g, ''))
  if (isNaN(numValue)) return String(val)

  // Format number with commas
  const formattedNumber = numValue.toLocaleString('en-US')

  return `${props.symbol}${formattedNumber}`
}

// Watch for changes in the value prop
watch(() => props.value, () => {
  if (props.animate) {
    animateToValue()
  }
})

onMounted(() => {
  if (props.animate) {
    animateToValue()
  }
})
</script>
